/**
 * Was im Prompt-Dialog steht — an EINER Stelle.
 *
 * Dieselbe Konvention wie shortcuts.js, hinweise.js, promptMass.js und
 * kontextRelevanz.js: die Aussage bekommt einen Namen, der Wortlaut daneben
 * ist frei. Der Test prüft Schlüsselmenge und Bindung, nie die Prosa.
 *
 * ⚠️ Warum der Dialog das überhaupt braucht: er zeigte bis zum 18.08. drei
 * Textfelder untereinander, ohne jede Auskunft darüber, welches davon gerade
 * bewertet. Die Angabe existiert (`GET /prompt-versions/effective-key`) und
 * wurde nur auf der Versionsseite benutzt — wer den Prompt hier bearbeitete,
 * konnte den falschen erwischen und merkte es an nichts.
 */

/**
 * Die Prompt-Bausteine des Dialogs, in Anzeigereihenfolge.
 *
 * `schluessel` ist der system_settings-Key und zugleich das Feld im Formular
 * — eine Zahl, eine Quelle: das Template baut die Blöcke daraus, statt sie
 * vierfach hinzuschreiben.
 *
 * `gegated` sagt, ob der Text durch das Regressions-Gate muss. ⚠️ Das ist
 * keine Kosmetik: bei `true` legt das Speichern einen ENTWURF an, der erst
 * nach einem bestandenen Vergleich aktiviert werden kann — beim Kommentar-
 * und Relevanz-Prompt wirkt dasselbe Speichern dagegen sofort. Wer den
 * Unterschied nicht sieht, wartet auf eine Wirkung, die schon da ist, oder
 * hält eine für gegeben, die noch aussteht.
 *
 * `hatVorgabe` markiert den einen Block, dessen Text auch OHNE Zeile in
 * system_settings wirksam ist: der Relevanz-Prompt fällt drüben auf eine
 * Konstante des Bewertungs-Backends zurück. ⚠️ Bis zum 24.08. stand das Feld
 * in diesem Fall leer da, und ein Satz darunter erklärte es — genau die
 * falsche Richtung: ein leeres Textfeld liest sich als "kein Prompt", und was
 * stattdessen läuft, stand nirgends. Jetzt holt der Dialog die geltende
 * Vorlage und zeigt sie; erklärt wird nichts mehr, weil der Text selbst da
 * steht (Simon, 24.08.). Die Marke trägt seitdem nur noch den AUSFALL —
 * lässt sich die Vorlage nicht laden, KANN das Feld leer sein, und dann muss
 * dastehen, warum.
 */
export const PROMPT_BLOECKE = [
  {
    schluessel: "llm_prompt_template",
    titel: "LLM-Prompt",
    gegated: true,
    hinweis: "Bewertet im Modus LLM. Änderungen werden als Entwurf gespeichert und wirken erst nach dem Gate.",
  },
  {
    schluessel: "rag_prompt_template",
    titel: "RAG-Prompt",
    gegated: true,
    hinweis: "Bewertet im Modus RAG. Änderungen werden als Entwurf gespeichert und wirken erst nach dem Gate.",
  },
  {
    schluessel: "comment_prompt_template",
    titel: "Kommentar-Prompt",
    gegated: false,
    hinweis: "Erzeugt die Sitzungskommentare. Bewertet nichts — wirkt sofort nach dem Speichern.",
  },
  {
    schluessel: "context_relevance_prompt_template",
    titel: "Kontext-Relevanz",
    gegated: false,
    // Der einzige Block mit einer Vorgabe im Backend -- s. `hatVorgabe`.
    hatVorgabe: true,
    hinweis: "Urteilt je Verlaufszeile, ob sie zum selben Gesprächsstrang gehört. Unabhängig vom Bewertungsmodus, ohne Versionierung.",
  },
];

/**
 * Die Sätze, mit denen der Dialog seinen eigenen Zustand erklärt.
 *
 * ⚠️ `unbekannt` ist der wichtigste davon. `openSystemSettingsModal` öffnet,
 * BEVOR die Auskunft da ist; solange sie fehlt, darf kein Block als aktiv
 * markiert sein. Eine falsche Markierung ist schlimmer als keine — sie
 * beantwortet genau die Frage, wegen der jemand hier ist.
 */
export const DIALOG = {
  aktiv: "wirkt jetzt",
  wirktNicht: "wirkt im aktuellen Modus nicht",
  unbekannt: "Welcher Prompt wirkt, wird noch geladen …",
  keinPrompt: "Im Modus ML wirkt kein Prompt — bewertet wird ohne Sprachmodell.",
  ohneGate: "ohne Gate",
  platzhalter: "Platzhalter",
  // ⚠️ Der Ausfall MUSS ausgeschrieben werden: sonst steht hier der Inhalt
  // von system_settings, und bei fehlender Zeile ist das eine leere Fläche,
  // die aussieht wie ein Prompt, den es nicht gibt.
  vorgabeFehlt: "Die geltende Vorlage ließ sich nicht laden — im Feld steht nur, was in system_settings gespeichert ist. Ist dort nichts, läuft trotzdem die Vorgabe des Backends.",
};

/**
 * Welcher Block der wirksame ist.
 *
 * `wirksam` ist die Antwort von /prompt-versions/effective-key oder null,
 * solange sie fehlt. Beides ergibt hier `false` — geraten wird nicht.
 */
export function istWirksam(block, wirksam) {
  if (!wirksam || !wirksam.prompt_key) return false;
  return wirksam.prompt_key === block.schluessel;
}
